import type { GameEvent, PlayerPublic } from '@estandarte/engine';
import { bossName, cardName, regionName, S, territoryName, theme } from '../theme';

type Players = (Pick<PlayerPublic, 'name'> & { color?: string })[];

const t = territoryName;

/** Texto curto para o log. Retorna null para eventos que não merecem linha. */
export function describeEvent(e: GameEvent, players: Players): string | null {
  const who = (p: number) => players[p]?.name ?? `Jogador ${p + 1}`;
  switch (e.type) {
    case 'setupPlaced':
      return `${who(e.player)} posicionou ${e.count} em ${t(e.territory)}.`;
    case 'placed':
      return `${who(e.player)} reforçou ${t(e.territory)} com ${e.count}.`;
    case 'setupDone':
      return 'Posicionamento concluído. Que comece a guerra!';
    case 'roundStart':
      return `— Rodada ${e.round} —`;
    case 'turnStart':
      return `Vez de ${who(e.player)}: ${e.reinforcements} reforços.`;
    case 'phase':
      return null;
    case 'trade':
      return `${who(e.player)} trocou 3 cartas por ${e.troops} tropas.`;
    case 'combat': {
      const notes: string[] = [];
      if (e.notes.includes('seaLimit')) notes.push('barco: máx. 2 dados');
      if (e.notes.includes('captain')) notes.push('Passagem do Capitão');
      if (e.notes.includes('range')) notes.push('ataque à distância');
      if (e.notes.includes('strength')) notes.push('Berserk +1');
      if (e.notes.includes('coordination')) notes.push('ataque coordenado +1');
      if (e.notes.includes('flow')) notes.push('Rajada de Harmonia +1');
      if (e.notes.includes('flowGuard')) notes.push('harmonia plena: no máximo 1 perda');
      if (e.notes.includes('knight')) notes.push(`${theme.vocations.defense.name} rerrolou a defesa`);
      if (e.notes.includes('blessing')) notes.push('Bênção: defesa vence tudo');
      if (e.notes.includes('precision')) notes.push('Bullseye: empates do atacante');
      if (e.notes.includes('ward')) notes.push('Stone Skin: no máximo 1 perda');
      if (e.notes.includes('luck')) notes.push('atacante usou o Amuleto da Sorte');
      if (e.notes.includes('luckDefender')) notes.push('defensor usou o Amuleto da Sorte');
      return `${who(e.attacker)} atacou ${t(e.to)} a partir de ${t(e.from)}: [${e.attackRolls.join(' ')}] × [${e.defenseRolls.join(' ')}] — atacante −${e.attackerLoss}, defensor −${e.defenderLoss}${notes.length ? ` (${notes.join('; ')})` : ''}.`;
    }
    case 'combatPreview':
      return `${who(e.attacker)} rolou [${e.attackRolls.join(' ')}] × [${e.defenseRolls.join(' ')}]…`;
    case 'conquest':
      return `${who(e.player)} conquistou ${t(e.to)}!`;
    case 'moveIn':
      return `${who(e.player)} moveu ${e.count} para ${t(e.to)}.`;
    case 'eliminated':
      return `${who(e.player)} foi eliminado por ${who(e.by)}${e.cards ? ` e perdeu ${e.cards} carta(s) para ele` : ''}.`;
    case 'missionChanged':
      return `Seu alvo foi eliminado por outro. Nova missão: ${S.fallbackMission(10)}`;
    case 'fortify':
      return `${who(e.player)} remanejou ${e.count} de ${t(e.from)} para ${t(e.to)}.`;
    case 'cardDrawn':
      return e.card ? `${who(e.player)} comprou a carta ${cardName(e.card)}.` : `${who(e.player)} comprou uma carta.`;
    case 'lootDrawn':
      return e.options.length
        ? `${who(e.player)} encontrou loot: ${e.options.map((k) => theme.loot[k].name).join(' ou ')}.`
        : `${who(e.player)} encontrou loot.`;
    case 'lootChosen':
      return e.kind ? `${who(e.player)} ficou com ${theme.loot[e.kind].name}.` : null;
    case 'lootPlayed':
      return describeLoot(e, who);
    case 'lootDiscarded':
      return `${who(e.player)} descartou ${theme.loot[e.kind].name} (limite de 3 cartas de loot).`;
    case 'reaction':
      return e.used ? null : `${who(e.player)} guardou o Amuleto da Sorte.`;
    case 'effectExpired': {
      const where = e.effect.territory ? t(e.effect.territory) : e.effect.link ? e.effect.link.map(t).join('–') : '';
      const names: Record<string, string> = {
        wall: `A Magic Wall entre ${where} caiu.`,
        ward: `O Stone Skin de ${where} acabou.`,
        paralyze: `${where} não está mais paralisado.`,
        frozen: `${where} descongelou.`,
      };
      return names[e.effect.kind] ?? null;
    }
    case 'power':
      return `${who(e.player)} (${theme.vocations.damage.name}) feriu ${t(e.target)}: −1 tropa.`;
    case 'heal':
      return `${who(e.player)} (${theme.vocations.heal.name}) curou 1 tropa em ${t(e.territory)}.`;
    case 'bossTimer':
      return e.timer > 0 ? `${bossName(e.boss)}: raid em ${e.timer} rodada(s).` : null;
    case 'bossRaid':
      return describeRaid(e, who);
    case 'bossRoll':
      return e.spawn ? `Dado do boss: ${e.roll}. Um boss vai surgir!` : `Dado do boss: ${e.roll}. Nada surge.`;
    case 'bossRegionRoll':
      return e.chooser !== null && e.regions[0] !== e.regions[1]
        ? `Dado de região: ${e.regions.map(regionName).join(' ou ')}. ${who(e.chooser)}, o mestre dos bosses, escolhe.`
        : `Dado de região: ${regionName(e.regions[0])}.`;
    case 'bossSpawn':
      return `${bossName(e.boss)} surgiu no covil de ${regionName(e.region)}!`;
    case 'bossCombat':
      return `${who(e.player)} atacou ${bossName(e.boss)}: [${e.attackRolls.join(' ')}] × [${e.defenseRolls.join(' ')}] — ${e.bossLoss ? `boss −${e.bossLoss}` : 'boss ileso'}, atacante −${e.attackerLoss}. Vida: ${e.hpLeft}.`;
    case 'bossDamageReward': {
      const guild = theme.guilds.find((g) => g.color === (players[e.player] as { color?: string })?.color)?.name ?? who(e.player);
      return `${guild} tirou ${e.damage} de vida de ${bossName(e.boss)} e comprou ${e.cards} carta${e.cards > 1 ? 's' : ''}.`;
    }
    case 'regionFirst': {
      const guild = theme.guilds.find((g) => g.color === (players[e.player] as { color?: string })?.color)?.name ?? who(e.player);
      const loot = e.loot ? `: ${theme.loot[e.loot].name}` : '';
      return `${guild} é a primeira guilda a dominar ${regionArticle(e.region)} e recebe 1 loot${loot}.`;
    }
    case 'harmony':
      return `${who(e.player)} soltou a Rajada de Harmonia (${e.spent}): +1 no maior dado até o fim do turno${e.guard ? ' e no máximo 1 perda por combate' : ''}.`;
    case 'bounty':
      return e.player === null
        ? 'Ninguém lidera sozinho: não há cabeça a prêmio nesta rodada.'
        : `Cabeça a prêmio: ${who(e.player)} tem mais territórios. A primeira conquista contra ele em cada turno vale 1 carta.`;
    case 'bountyClaimed':
      return `${who(e.player)} cobrou a recompensa pela cabeça de ${who(e.target)} e comprou 1 carta.`;
    case 'skull':
      return `${who(e.player)} conquistou demais neste turno e ganhou a caveira vermelha.`;
    case 'skullPenalty': {
      const lost = [e.card ? '1 carta' : '', e.loot ? '1 loot' : ''].filter(Boolean).join(' e ');
      return `${who(e.player)} tinha a caveira e perdeu um território para ${who(e.by)}${lost ? `: descartou ${lost}` : ''}.`;
    }
    case 'bossKilled':
      return `${who(e.player)} deu o último golpe em ${bossName(e.boss)}!`;
    case 'missionComplete':
      return `${who(e.player)} cumpriu a missão secreta!`;
    case 'gameOver':
      return `Fim de jogo: ${S.scoreReasons[e.result.reason]}. Vitória de ${who(e.result.winner)}.`;
  }
}

const list = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`);

function describeLoot(e: Extract<GameEvent, { type: 'lootPlayed' }>, who: (p: number) => string): string {
  const name = theme.loot[e.kind].name;
  const p = who(e.player);
  const hits = (e.hits ?? []).map((h) => (h.lost ? `${t(h.territory)} −${h.lost}` : `${t(h.territory)} já estava com 1`));
  switch (e.kind) {
    case 'strike':
    case 'barrage':
      return `${p} usou ${name}: ${hits.join(', ') || 'nenhuma tropa removida'}.`;
    case 'strength':
      return `${p} bebeu ${name}: +1 no maior dado de ataque até o fim do turno.`;
    case 'precision':
      return `${p} bebeu ${name}: em todos os combates deste turno, os empates ficam com ele.`;
    case 'captain':
      return `${p} usou ${name}: neste turno, um ataque de porto a porto inimigo, com até 3 dados.`;
    case 'blessing':
      return `${p} abençoou ${t(e.territory!)}: no próximo combate, a defesa vence tudo.`;
    case 'wall':
      return `${p} ergueu uma ${name} entre ${t(e.from!)} e ${t(e.to!)} até o próximo turno dele.`;
    case 'restore':
      return `${p} usou ${name}: +${e.count} tropas em ${t(e.territory!)}.`;
    case 'ward':
      return `${p} usou ${name} em ${t(e.territory!)}: perde no máximo 1 tropa por rolagem até o próximo turno dele.`;
    case 'paralyze':
      return `${p} paralisou ${t(e.territory!)}: no próximo turno do dono, não ataca nem se move.`;
    case 'luck':
      return `${p} usou o ${name} e rerrolou os dados.`;
    case 'teleport':
      return `${p} usou ${name}: ${e.count} tropas de ${t(e.from!)} para ${t(e.to!)}.`;
    case 'reinforce':
      return `${p} usou ${name}: +${e.count} tropas para posicionar.`;
    case 'draw':
      return e.cards ? `${p} usou ${name} e comprou ${e.cards.map(cardName).join(' e ')}.` : `${p} usou ${name} e comprou 2 cartas.`;
    case 'steal':
      return e.cards
        ? `${p} usou ${name} e pegou ${cardName(e.cards[0])} de ${who(e.victim!)}.`
        : `${p} usou ${name} e pegou uma carta de ${who(e.victim!)}.`;
  }
}

function describeRaid(e: Extract<GameEvent, { type: 'bossRaid' }>, who: (p: number) => string): string {
  const boss = bossName(e.boss);
  const hit = e.losses.filter((l) => l.lost > 0).map((l) => `${t(l.territory)} −${l.lost}`);
  const losses = hit.length ? hit.join(', ') : 'ninguém perdeu tropas';
  switch (e.raid) {
    case 'freeze':
      return `${boss} congelou ${list(e.frozen.map(t))} (${losses}). Nesta rodada eles não atacam nem recebem tropas.`;
    case 'quake':
      return `${boss} fez a terra tremer em volta do covil de ${regionName(e.region)}: ${losses}.`;
    case 'halveStrongest':
      return `${boss} caiu sobre o exército mais forte perto do covil: ${losses}.`;
    case 'damageAdjacentAndDiscard': {
      const lost = e.discards.filter((d) => d.count > 0).map((d) => who(d.player));
      return `${boss} arrasou os vizinhos do covil (${losses})${lost.length ? ` e ${list(lost)} perderam 1 carta` : ''}.`;
    }
    default:
      return `${boss} fez a raid em ${regionName(e.region)}: ${losses}.`;
  }
}

/** "o Gelo", "as Ilhas", "Darama"… */
function regionArticle(region: string): string {
  const name = regionName(region);
  const art: Record<string, string> = { north: 'o ', south: 'o ', ice: 'o ', isles: 'as ', darama: '', far: 'as ' };
  return `${art[region] ?? ''}${name}`;
}
